/* eslint-disable react/prop-types */

import React, { useState, useRef, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Select, { components } from 'react-select';
import { Form, Col } from 'react-bootstrap';
import { FormLabel } from './FormLabel';
import ErrorFeedback from './ErrorFeedback';

const CustomValueContainer = ({ children, ...props }) => {
  const value = props.getValue();
  const isMulti = props.selectProps.isMulti;

  return (
    <components.ValueContainer {...props}>
      {isMulti && value.length > 2 ? (
        <span>{`${value.length} options selected`}</span>
      ) : (
        children
      )}
    </components.ValueContainer>
  );
};

const DynamicDropdown = ({
  metadata,
  control,
  errors,
  dependentFieldValue,
  isDisabled
}) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const selectRef = useRef(null);
  const { setValue, getValues } = useFormContext();

  // Transform options to grouped Select format
  const formatGroupedOptions = options => {
    if (!options) return [];

    // Check if the options are grouped (have sectionLabel)
    if (options[0]?.sectionLabel) {
      return options.map(group => ({
        label: group.sectionLabel,
        options: group.options.map(option => ({
          value: option.value,
          label: option.label
        }))
      }));
    }

    // Return regular options if not grouped
    return options.map(option => ({
      value: option.value,
      label: option.label
    }));
  };

  const selectOptions = formatGroupedOptions(metadata.options);

  // Helper to flatten options
  const flattenOptions = options => {
    return options.reduce((acc, group) => {
      if (group.options) {
        return [...acc, ...group.options];
      }
      return [...acc, group];
    }, []);
  };

  const flatOptions = flattenOptions(selectOptions);

  // Helper to format value based on single/multi select
  const formatValue = (options, value) => {
    if (!value) return metadata.multiSelect ? [] : null;

    const findOption = val => flatOptions.find(opt => opt.value === val);

    return metadata.multiSelect
      ? (Array.isArray(value) ? value : [value]).map(findOption).filter(Boolean)
      : findOption(value);
  };

  // Handle section header click
  const handleSectionClick = (group, value, onChange) => {
    if (!metadata.multiSelect) return;
    const groupValues = group.options.map(option => option.value);

    const isGroupSelected = groupValues.every(val =>
      Array.isArray(value) ? value.includes(val) : false
    );

    const newValue = isGroupSelected
      ? value.filter(val => !groupValues.includes(val))
      : [...(Array.isArray(value) ? value : []), ...groupValues];

    onChange(newValue);
  };

  // Handle "Select All" functionality
  const handleSelectAllClick = (value, onChange) => {
    if (!metadata.multiSelect) return;
    const allValues = flatOptions.map(option => option.value);

    const isAllSelected = allValues.every(val =>
      Array.isArray(value) ? value.includes(val) : false
    );

    const newValue = isAllSelected ? [] : allValues;
    onChange(newValue);
  };

  // Handle click outside
  const handleClickOutside = event => {
    if (selectRef.current && !selectRef.current.contains(event.target)) {
      setMenuIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!metadata.multiSelect && flatOptions.length === 1) {
      const newValue = flatOptions[0].value;
      const currentValue = getValues(metadata.id);
      if (currentValue !== newValue) {
        setValue(metadata.id, newValue);
      }
    }
    if (metadata.dependsOn && !dependentFieldValue) {
      setValue(metadata.id, null);
    }
  }, []);

  const customStyles = {
    control: provided => ({
      ...provided,
      minHeight: '80px',
      backgroundColor: 'white',
      ':focus-within': {
        boxShadow:
          'inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 5px var(--falcon-soft-primary) !important;'
      }
    }),
    valueContainer: provided => ({
      ...provided,
      whiteSpace: 'nowrap',
      overflowY: 'auto',
      textOverflow: 'ellipsis',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignContent: 'flex-start',
      maxHeight: '90px'
    }),
    multiValue: provided => ({
      ...provided,
      height: 'fit-content'
    }),
    multiValueLabel: provided => ({
      ...provided
    })
  };

  return (
    <Col>
      <Form.Group as={Col} sm={12} md={12} className="mb-3">
        <FormLabel
          label={metadata.label}
          required={metadata?.validation?.required}
          info_key={metadata?.info_key}
        />
        <div ref={selectRef}>
          <Controller
            name={metadata.id}
            control={control}
            defaultValue={metadata.multiSelect ? [] : null}
            rules={{ required: metadata.validation?.required }}
            render={({ field: { value, onChange, ...field } }) => (
              <Select
                {...field}
                isMulti={metadata.multiSelect}
                isDisabled={
                  !metadata.isEditable ||
                  (metadata.dependsOn && !dependentFieldValue) ||
                  isDisabled
                }
                defaultValue={metadata.defaultValue}
                isClearable={true}
                styles={customStyles}
                options={[
                  ...(metadata.multiSelect
                    ? [
                        {
                          label: 'Select All',
                          value: 'select-all',
                          isSelectAll: true
                        }
                      ]
                    : []),
                  ...selectOptions
                ]}
                value={formatValue(selectOptions, value)}
                onChange={(selectedOptions, actionMeta) => {
                  if (metadata.multiSelect) {
                    if (!selectedOptions) {
                      onChange(null);
                      return;
                    }

                    const selectedValues = Array.isArray(selectedOptions)
                      ? selectedOptions.map(option => option.value)
                      : [];

                    // Handle "Select All" click
                    if (selectedOptions.some(opt => opt.isSelectAll)) {
                      handleSelectAllClick(value, onChange);
                    } else {
                      onChange(
                        selectedValues.length === 0 ? null : selectedValues
                      );
                    }
                  } else {
                    onChange(selectedOptions ? selectedOptions.value : null);
                    setMenuIsOpen(false); // Close menu after single select
                  }
                }}
                classNamePrefix="react-select"
                className={errors[metadata.id] ? 'is-invalid' : ''}
                components={{
                  GroupHeading: props => (
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: '0.9em',
                        color: '#666',
                        cursor: 'pointer'
                      }}
                      onClick={() =>
                        handleSectionClick(props.data, value, onChange)
                      }
                    >
                      {props.children}
                    </div>
                  ),
                  ValueContainer: CustomValueContainer
                }}
                menuIsOpen={menuIsOpen}
                onMenuOpen={() => setMenuIsOpen(true)}
                onMenuClose={() => setMenuIsOpen(false)}
                closeMenuOnSelect={!metadata.multiSelect}
                hideSelectedOptions={false}
              />
            )}
          />
        </div>
        <ErrorFeedback key={metadata.id} error={errors[metadata.id]} />
      </Form.Group>
    </Col>
  );
};

export default DynamicDropdown;
